import React, { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import MenuIcon from "@mui/icons-material/Menu";
import ToggleColorMode from "./ToggleColorMode";
import { useNavigate } from "react-router-dom";
import UserMenu from "../components/UserMenu";
import { COMPANY_SEARCH_NAME_URL } from "../constants";
import MobileMenu from "../components/MobileMenu";
import SearchBar from "../components/SearchBar";
import { debounce } from "../constants/utility";

function AppAppBar({
  showToggle = true,
  showSubMenu = true,
  showPricing = false,
  showContactUs = false,
  showTermsofService = false,
  showPrivacy = false,
}) {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [options, setOptions] = useState([]);
  const navigate = useNavigate();

  //get user from redux store
  const user = useSelector((state) => state.user.user);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const scrollToSection = (sectionId) => {
    const sectionElement = document.getElementById(sectionId);
    const offset = 128;
    if (sectionElement) {
      const targetScroll = sectionElement.offsetTop - offset;
      sectionElement.scrollIntoView({ behavior: "smooth" });
      window.scrollTo({
        top: targetScroll,
        behavior: "smooth",
      });
      setOpen(false);
    }
  };

  const debouncedHandleSearch = debounce(async (value) => {
    try {
      if (value.length >= 3) {
        setIsLoading(true);
        const response = await fetch(`${COMPANY_SEARCH_NAME_URL}${value}`);

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        const names = data.map((item) => ({ name: item.name, cin: item.cin }));
        setOptions(names);
        setIsLoading(false);
      } else {
        setOptions([]);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching search results:", error);
      setOptions([]);
      setIsLoading(false);
    }
  }, 400);

  const handleSearch = (value) => {
    debouncedHandleSearch(value);
  };

  const handleSignUp = () => {
    navigate(`/auth`);
  };
  const handleChange = useCallback(
    (event, newValue) => {
      if (newValue && newValue.name) {
        setSearchValue(newValue.name);
        navigate(`/search/${newValue.name}`, { state: { cin: newValue.cin } });
      }
    },
    [navigate]
  );

  return (
    <div>
      <AppBar
        position="fixed"
        sx={{
          boxShadow: 0,
          bgcolor: "transparent",
          backgroundImage: "none",
          mt: 2,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            variant="regular"
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
              borderRadius: "999px",
              bgcolor:
                theme.palette.mode === "light"
                  ? "rgba(255, 255, 255, 0.3)"
                  : "rgba(0, 0, 0, 0.25)",
              backdropFilter: "blur(24px)",
              maxHeight: 40,
              border: "1px solid",
              borderColor: "divider",
              boxShadow:
                theme.palette.mode === "light"
                  ? `0 0 1px #750AC2, 1px 1.5px 2px -1px #750AC2, 4px 4px 12px -2.5px #750AC2`
                  : "0 0 1px #750AC2, 1px 1.5px 2px -1px #750AC2, 4px 4px 12px -2.5px #750AC2",
            })}
          >
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                ml: "-18px",
                px: 0,
              }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: 16, sm: 18, md: 20, lg: 24 },
                  fontWeight: 600,
                  color: "text.primary",
                  marginX: 2,
                  cursor: "pointer",
                }}
                onClick={() => navigate("/")}
              >
                Check Before Invest
              </Typography>

              <Box sx={{ display: { xs: "none", md: "flex" } }}>
                <MenuItem style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}>
                  <SearchBar
                    options={options}
                    searchValue={searchValue}
                    handleSearch={handleSearch}
                    handleChange={handleChange}
                    isLoading={isLoading}
                  />
                </MenuItem>
                {showPricing && (
                  <MenuItem
                    onClick={() => navigate("/pricing")}
                    sx={{
                      py: "6px",
                      px: "12px",
                    }}
                  >
                    <Typography variant="body2" color="text.primary">
                      Pricing
                    </Typography>
                  </MenuItem>
                )}
                {showContactUs && (
                  <MenuItem
                    onClick={() => navigate("/contact-us")}
                    sx={{
                      py: "6px",
                      px: "12px",
                    }}
                  >
                    <Typography variant="body2" color="text.primary">
                      Contact Us
                    </Typography>
                  </MenuItem>
                )}
                {showSubMenu && (
                  <MenuItem
                    onClick={() => scrollToSection("testimonials")}
                    sx={{ py: "6px", px: "12px" }}
                  >
                    <Typography variant="body2" color="text.primary">
                      Risk Report
                    </Typography>
                  </MenuItem>
                )}
                {showSubMenu && (
                  <MenuItem
                    onClick={() => scrollToSection("highlights")}
                    sx={{ py: "6px", px: "12px" }}
                  >
                    <Typography variant="body2" color="text.primary">
                      EWS
                    </Typography>
                  </MenuItem>
                )}

                {showSubMenu && (
                  <MenuItem
                    onClick={() => scrollToSection("Home_document")}
                    sx={{ py: "6px", px: "12px" }}
                  >
                    <Typography variant="body2" color="text.primary">
                      Document
                    </Typography>
                  </MenuItem>
                )}
                {showSubMenu && (
                  <MenuItem
                    onClick={() => navigate("/Contact-us")}
                    sx={{ py: "6px", px: "12px" }}
                  >
                    <Typography variant="body2" color="text.primary">
                      Contact Us
                    </Typography>
                  </MenuItem>
                )}

                {showTermsofService && (
                  <MenuItem
                    onClick={() => {
                      navigate("/terms-of-service");
                    }}
                    sx={{ py: "6px", px: "12px" }}
                  >
                    <Typography variant="body2" color="text.primary">
                      Term of Service
                    </Typography>
                  </MenuItem>
                )}
                {showPrivacy && (
                  <MenuItem
                    onClick={() => {
                      navigate("/Privacy");
                    }}
                    sx={{ py: "6px", px: "12px" }}
                  >
                    <Typography variant="body2" color="text.primary">
                      Privacy
                    </Typography>
                  </MenuItem>
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              {showToggle && <ToggleColorMode />}
              {!user ? (
                <>
                  <Button
                    color="primary"
                    variant="text"
                    size="small"
                    onClick={handleSignUp}
                  >
                    Sign in
                  </Button>
                  <Button
                    color="primary"
                    variant="contained"
                    size="small"
                    onClick={handleSignUp}
                  >
                    Sign up
                  </Button>
                </>
              ) : (
                <UserMenu user={user} />
              )}
            </Box>
            <Box sx={{ display: { sm: "", md: "none" } }}>
              <Button
                variant="text"
                color="primary"
                aria-label="menu"
                onClick={toggleDrawer(true)}
                sx={{ minWidth: "30px", p: "4px" }}
              >
                <MenuIcon />
              </Button>
              <MobileMenu
                open={open}
                setOpen={setOpen}
                toggleDrawer={toggleDrawer}
                options={options}
                handleChange={handleChange}
                handleSearch={handleSearch}
                searchValue={searchValue}
                handleSignUp={handleSignUp}
                showPricing={showPricing}
                showContactUs={showContactUs}
                showPrivacy={showPrivacy}
                showTermsAndCondition={showTermsofService}
              />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
    </div>
  );
}

export default AppAppBar;
